import { useState, useEffect } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';

const STATUS_COLOR = {
  pending:  'bg-amber-100 text-amber-700 border-amber-200',
  approved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-100 text-red-700 border-red-200',
};

export default function GroupRequests() {
  const { T } = useLang();
  const G = T.groupRequests;

  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('pending');
  const [actionId, setActionId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [toast, setToast] = useState('');

  const statusLabel = {
    pending:  G.statusPending,
    approved: G.statusApproved,
    rejected: G.statusRejected,
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/group-requests');
      setRequests(data.requests);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const handleApprove = async (id) => {
    setActionId(id);
    try {
      await api.post(`/group-requests/${id}/approve`);
      showToast(G.toastApproved);
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error');
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    setActionId(rejectTarget.id);
    try {
      await api.post(`/group-requests/${rejectTarget.id}/reject`, { note: rejectNote });
      showToast(G.toastRejected);
      setRejectTarget(null);
      setRejectNote('');
      load();
    } catch (err) {
      showToast(err.response?.data?.error || 'Error');
    } finally {
      setActionId(null);
    }
  };

  const filtered = requests.filter(r => filter === 'all' || r.status === filter);
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  const filters = [
    ['pending',  G.filterPending],
    ['approved', G.filterApproved],
    ['rejected', G.filterRejected],
    ['all',      G.filterAll],
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 rounded-xl bg-gray-900 text-white px-5 py-3 text-sm shadow-xl">
          {toast}
        </div>
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl p-6">
            <h3 className="text-base font-bold text-gray-800 mb-1">{G.rejectTitle}</h3>
            <p className="text-sm text-gray-500 mb-4">
              {G.labelGroupName}: <strong>{rejectTarget.group_name}</strong>
            </p>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              {G.rejectLabel}
            </label>
            <textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              rows={3}
              className="w-full rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
              placeholder={G.rejectPlaceholder}
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => { setRejectTarget(null); setRejectNote(''); }}
                className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                {G.btnCancel}
              </button>
              <button
                onClick={handleReject}
                disabled={actionId === rejectTarget.id}
                className="flex-1 rounded-xl bg-red-600 hover:bg-red-700 py-2.5 text-sm font-semibold text-white transition-colors disabled:opacity-60"
              >
                {actionId === rejectTarget.id ? G.btnRejecting : G.btnConfirmReject}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">{G.title}</h1>
            {pendingCount > 0 && (
              <span className="rounded-full bg-amber-500 text-white text-xs font-bold px-2.5 py-0.5">
                {pendingCount}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500">{G.subtitle}</p>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-5 flex-wrap">
          {filters.map(([val, lbl]) => (
            <button
              key={val}
              onClick={() => setFilter(val)}
              className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
                filter === val
                  ? 'bg-emerald-600 text-white'
                  : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {lbl}
              {val === 'pending' && pendingCount > 0 && (
                <span className="ml-1.5 bg-amber-400 text-white text-xs rounded-full px-1.5 py-px">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-gray-400">{G.loading}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-3">📭</div>
            <p>{G.empty}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(req => (
              <div key={req.id} className="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="text-base font-bold text-gray-900">{req.group_name}</h3>
                        <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${STATUS_COLOR[req.status]}`}>
                          {statusLabel[req.status]}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm text-gray-600">
                        <div><span className="text-gray-400">{G.labelChair}:</span> <strong>{req.chair_name}</strong></div>
                        <div><span className="text-gray-400">{G.labelPhone}:</span> <strong>{req.chair_phone}</strong></div>
                        <div><span className="text-gray-400">{G.labelMembers}:</span> <strong>{req.member_count}</strong></div>
                      </div>
                      {req.rejection_note && (
                        <p className="mt-2 text-xs text-red-500 bg-red-50 rounded-lg px-3 py-1.5">
                          {G.labelRejectionNote}: {req.rejection_note}
                        </p>
                      )}
                      {req.approved_group_id && (
                        <p className="mt-2 text-xs text-emerald-600">{G.labelGroupId}: #{req.approved_group_id}</p>
                      )}
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap shrink-0">
                      {new Date(req.created_at).toLocaleDateString('sw-TZ', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>

                  {req.status === 'pending' && (
                    <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
                      <button
                        onClick={() => handleApprove(req.id)}
                        disabled={actionId === req.id}
                        className="flex-1 rounded-xl bg-emerald-600 hover:bg-emerald-700 py-2.5 text-sm font-bold text-white transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                      >
                        {actionId === req.id ? (
                          <><svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> {G.btnApproving}</>
                        ) : G.btnApprove}
                      </button>
                      <button
                        onClick={() => setRejectTarget(req)}
                        disabled={!!actionId}
                        className="flex-1 rounded-xl border-2 border-red-200 text-red-600 hover:bg-red-50 py-2.5 text-sm font-bold transition-colors disabled:opacity-50"
                      >
                        {G.btnReject}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
